import { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "../../lib/content";
import { Btn } from "./Btn";

export const Logo = ({ className = "" }) => (
  <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2 group ${className}`}>
    <span className="relative w-9 h-9 rounded-full bg-ocean border-2 border-navy grid place-items-center overflow-hidden group-hover:rotate-12 transition-transform">
      <svg viewBox="0 0 32 32" className="w-6 h-6">
        <path d="M4 20 C 10 12, 20 12, 27 18 L 30 12 L 28 20 C 24 24, 12 26, 4 20 Z" fill="#FDFBF7" />
        <circle cx="22" cy="18" r="1.6" fill="#082F49" />
        <path d="M8 20 L 4 26 L 12 22" fill="#38BDF8" />
      </svg>
    </span>
    <span className="font-display font-extrabold text-xl tracking-tight text-navy">
      Bleu<span className="text-ocean">Sharks</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-cream/85 backdrop-blur-xl border-b-2 border-navy/10" : "bg-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between" data-testid="navbar">
        <Logo />
        <div className="hidden lg:flex items-center gap-1">
          {NAV.slice(1, 6).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full font-body font-medium text-sm transition-colors ${isActive ? "bg-navy text-cream" : "text-navy hover:bg-navy/10"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="hidden lg:block">
          <Btn to="/contact" variant="yellow" testid="nav-cta" className="!px-6 !py-2.5 !text-sm">Tell Us What You Need</Btn>
        </div>
        <button
          className="lg:hidden w-11 h-11 grid place-items-center rounded-full border-2 border-navy bg-white shadow-brutal"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-cream border-b-2 border-navy px-6 pb-8 pt-2" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl font-display font-bold text-lg ${isActive ? "bg-navy text-cream" : "text-navy"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <Btn to="/contact" variant="yellow" testid="mobile-nav-cta" className="w-full mt-4">Tell Us What You Need</Btn>
        </div>
      )}
    </header>
  );
};
